import React, {useEffect, useRef, useState} from 'react';
import {useHistory} from "react-router-dom";
import {useAuthState} from '../../services/firebase';
import {useEdges} from '../../../features/edges/context/EdgesContext';
import Activity from "@mui/icons-material/BarChartRounded";
import GearFill from "@mui/icons-material/SettingsRounded";
import Speedometer2 from "@mui/icons-material/SpeedRounded";
import MenuIcon from "@mui/icons-material/MenuRounded";
import {Button, Form} from 'react-bootstrap';
import ThemeToggle from '../ThemeToggle';
import Breadcrumb from './Breadcrumb';
import useBreadcrumbs from '../../hooks/useBreadcrumbs';
import {useSidebar} from '../../context/SidebarContext';
import {isMonitoredLifecycle} from '../../../features/edges/constants/edgesConstants';

const Navigation = () => {
  const authState = useAuthState();
  const history = useHistory();
  const {edges} = useEdges();
  const breadcrumbs = useBreadcrumbs();
  const {toggleMobileSidebar} = useSidebar();

  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(window.scrollY);

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredEdges, setFilteredEdges] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  // Filter edges when query changes
  useEffect(() => {
    if (searchQuery.length > 0) {
      const re = new RegExp(searchQuery, "i");
      const results = edges.filter(element =>
        element.name?.match(re) ||
        element.address?.street?.match(re) ||
        element.address?.city?.match(re) ||
        element.id?.match(re)
      );
      setFilteredEdges(results.slice(0, 10)); // Limit to 10 results
      setShowDropdown(true);
    } else {
      setFilteredEdges([]);
      setShowDropdown(false);
    }
  }, [searchQuery, edges]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchRef]);


  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setShowDropdown(false);
    history.push(`/?search=${encodeURIComponent(searchQuery)}`);
  };

  const navigateTo = (path) => {
    setShowDropdown(false);
    history.push(path);
  }


  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setHidden(true); // hide on scroll down
        setShowDropdown(false);
      } else {
        setHidden(false); // show on scroll up
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  const getStatusClass = (edge) => {
    if (!isMonitoredLifecycle(edge.lifecycle?.status) || !edge.currentStates?.deadmanCheck) return "status-unknown";
    if (edge.currentStates.deadmanCheck.type === 401) return "status-issue";
    return "status-active";
  };

  return (
    <nav className={`navbar-custom ${hidden ? 'navbar-hidden' : ''}`}>
      <div className="navbar-container container-fluid">

        {/* Left Block: Breadcrumb only */}
        <div className="navbar-left">
          {/* Logo moved to Sidebar */}
          {authState.user && (
            <>
              <Button
                variant="link"
                className="mobile-nav-toggle p-1 me-2 text-reset"
                onClick={() => toggleMobileSidebar(true)}
              >
                <MenuIcon/>
              </Button>
              <Breadcrumb paths={breadcrumbs}/>
            </>
          )}
        </div>

        {/* Center Block: Search */}
        <div className="navbar-center flex-grow-1 d-flex justify-content-center">
          {authState.user && (
            <div className="position-relative search-wrapper-center" ref={searchRef}>
              <Form onSubmit={handleSearchSubmit} className="w-100">
                <div className="search-input-group">
                  <Form.Control
                    type="search"
                    placeholder="Search SCCs..."
                    value={searchQuery}
                    className="scc-header-search-input-new"
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery && setShowDropdown(true)}
                  />
                </div>
              </Form>
              {showDropdown && filteredEdges.length > 0 && (
                <div className="search-results-dropdown">
                  {filteredEdges.map(edge => (
                    <div key={edge.id}
                         className={`search-result-item scc-search-item ${getStatusClass(edge)}`}>
                      <div className="text-truncate me-2">
                        <div className="fw-bold">{edge.name || edge.id}</div>
                        <div className="small text-muted text-truncate">{edge.address}</div>
                      </div>
                      <div className="d-flex text-nowrap">
                        <Button variant="link" className="p-1" title="Statistics"
                                onClick={() => navigateTo(`/edges/${edge.id}/statistics`)}>
                          <Activity size={16}/>
                        </Button>
                        <Button variant="link" className="p-1" title="Configuration"
                                onClick={() => navigateTo(`/edges/${edge.id}/configuration`)}>
                          <GearFill size={16}/>
                        </Button>
                        {edge.meterReport && (
                          <Button variant="link" className="p-1" title="Meter Report"
                                  onClick={() => navigateTo(`/edges/${edge.id}/meterreport`)}>
                            <Speedometer2 size={16}/>
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Block: Theme Toggle only */}
        <div className="navbar-right">
          {/* Tenants, Docs, Logout moved to sidebar */}
          <ThemeToggle/>
        </div>

      </div>
    </nav>
  );
};

export default Navigation;
